
import { useCallback, useEffect, useState } from "react";
import { grnOfflineRepository } from "../repositories/grnRepository";
import { onNetworkStatusChange } from "../utils/network";

const getGRNSaveMessage = (response) => {
  const data = response?.data;

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  if (typeof data?.response === "string" && data.response.trim()) {
    return data.response;
  }

  if (
    typeof data?.response?.message === "string" &&
    data.response.message.trim()
  ) {
    return data.response.message;
  }

  return "";
};

const isSuccessfulGrnSave = (response) => {
  const status = response?.status;
  const message = getGRNSaveMessage(response).toLowerCase();
  return (
    (status >= 200 && status < 300) &&
    (!message ||
      message.includes("saved successfully") ||
      message.includes("success") ||
      message.includes("already exists"))
  );
};

export const useGRNOffline = (authToken, userToken, queryOptions = {}) => {
  const {
    page = 1,
    pageSize = 10,
    searchKey = "",
    filter = "",
    sortKey = "created_at",
    sortOrder = "desc",
    orgToken = userToken,
  } = queryOptions || {};
  const [grnList, setGRNList] = useState([]);
  const [paginationData, setPaginationData] = useState({
    total_count: 0,
    current_page_count: 0,
    page_count: 0,
    page_size: pageSize,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : false
  );

  const refreshGRN = useCallback(async () => {
    if (!userToken) {
      setGRNList([]);
      setPaginationData({
        total_count: 0,
        current_page_count: 0,
        page_count: 0,
        page_size: pageSize,
      });
      setLoading(false);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const data = await grnOfflineRepository.getGRNData(authToken, userToken, {
        page,
        pageSize,
        searchKey,
        filter,
        sortKey,
        sortOrder,
        orgToken,
      });
      const rows = Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data)
          ? data
          : [];
      setGRNList(rows);
      setPaginationData({
        total_count: Number(data?.pagination?.total_count || rows.length || 0),
        current_page_count: Number(
          data?.pagination?.current_page_count || rows.length || 0,
        ),
        page_count: Number(data?.pagination?.page_count || 0),
        page_size: Number(data?.pagination?.page_size || pageSize),
      });
      return rows;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [authToken, orgToken, page, pageSize, searchKey, filter, sortKey, sortOrder, userToken]);

  const loadInvoices = useCallback(async () => {
    if (!userToken) {
      return [];
    }

    try {
      return await grnOfflineRepository.getInvoices(authToken, orgToken || userToken);
    } catch (err) {
      setError(err);
      return [];
    }
  }, [authToken, orgToken, userToken]);

  const createGRN = useCallback(
    async (grnPayload = []) => {
      if (!userToken) {
        return { success: false, offline: false };
      }

      setError(null);

      try {
        if (typeof navigator !== "undefined" && !navigator.onLine) {
          await grnOfflineRepository.createOfflineGRN(grnPayload, userToken);
          const cached = await grnOfflineRepository.getGRNFromIndexedDB();
          setGRNList(cached);
          return { success: true, offline: true };
        }

        const response = await grnOfflineRepository.createGRNOnline(
          authToken,
          grnPayload,
          userToken,
        );

        if (!isSuccessfulGrnSave(response)) {
          throw new Error(
            getGRNSaveMessage(response) || "Failed to save GRN data",
          );
        }

        refreshGRN().catch((refreshErr) => {
          console.error("GRN refresh after save failed:", refreshErr);
        });

        return {
          success: true,
          offline: false,
          message: getGRNSaveMessage(response) || "GRN Added Successfully",
        };
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [authToken, refreshGRN, userToken]
  );

  const syncPendingGRN = useCallback(async () => {
    if (!authToken || !userToken) {
      return [];
    }

    try {
      const result = await grnOfflineRepository.syncOfflineGRN(
        authToken,
        userToken
      );

      if (result.length > 0) {
        await refreshGRN();
      }

      return result;
    } catch (err) {
      setError(err);
      return [];
    }
  }, [authToken, refreshGRN, userToken]);

  useEffect(() => {
    refreshGRN();
  }, [refreshGRN]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    const unsubscribe = onNetworkStatusChange(
      async () => {
        setIsOnline(true);
        await loadInvoices();
        await syncPendingGRN();
        await refreshGRN();
      },
      () => setIsOnline(false)
    );

    return unsubscribe;
  }, [loadInvoices, refreshGRN, syncPendingGRN]);

  return {
    grnList,
    paginationData,
    loading,
    error,
    isOnline,
    createGRN,
    refreshGRN,
  };
};
